/**
 * Zod Validation Schemas for API Responses
 *
 * Ensures runtime type safety for all API responses
 * Prevents malformed data from causing UI errors
 */

import { z } from 'zod';

// ============================================
// COMMON SCHEMAS
// ============================================

/**
 * Base API Response wrapper
 */
export const ApiResponseSchema = <T extends z.ZodType>(dataSchema: T) =>
  z.object({
    success: z.boolean(),
    data: dataSchema.optional(),
    error: z.object({
      code: z.string(),
      message: z.string()
    }).optional(),
    cached: z.boolean().optional(),
    timestamp: z.number()
  });

// ============================================
// ADVICE BOT API SCHEMAS
// ============================================

/**
 * Twitter/X Sentiment Schema
 */
export const TwitterSentimentSchema = z.object({
  symbol: z.string(),
  mentions: z.number().int().min(0),
  sentiment: z.number().min(-100).max(100),
  influencerMentions: z.number().int().min(0),
  followers: z.number().int().min(0),
  trend: z.enum(['up', 'down', 'stable']),
  timestamp: z.number()
});

/**
 * Reddit Sentiment Schema
 */
export const RedditSentimentSchema = z.object({
  symbol: z.string(),
  mentions: z.number().int().min(0),
  sentiment: z.number().min(-100).max(100),
  upvoteRatio: z.number().min(0).max(1),
  trend: z.enum(['up', 'down', 'stable']),
  subreddits: z.array(z.string()),
  timestamp: z.number()
});

/**
 * News Sentiment Schema
 */
export const NewsSentimentSchema = z.object({
  symbol: z.string(),
  mentions: z.number().int().min(0),
  sentiment: z.number().min(-100).max(100),
  sources: z.array(z.string()),
  trend: z.enum(['up', 'down', 'stable']),
  topHeadlines: z.array(z.object({
    title: z.string(),
    url: z.string().url(),
    source: z.string(),
    sentiment: z.enum(['positive', 'negative', 'neutral'])
  })),
  timestamp: z.number()
});

/**
 * Aggregated Sentiment Schema
 */
export const AggregatedSentimentSchema = z.object({
  symbol: z.string(),
  twitter: TwitterSentimentSchema,
  reddit: RedditSentimentSchema,
  news: NewsSentimentSchema,
  overall: z.object({
    score: z.number().min(-100).max(100),
    sentiment: z.enum(['bullish', 'neutral', 'bearish']),
    confidence: z.number().min(0).max(100)
  }),
  timestamp: z.number()
});

/**
 * Technical Indicators Schema
 */
export const TechnicalIndicatorsSchema = z.object({
  symbol: z.string(),
  timeframe: z.string(),
  rsi: z.number().min(0).max(100),
  macd: z.object({
    value: z.number(),
    signal: z.number(),
    histogram: z.number()
  }),
  bollingerBands: z.object({
    upper: z.number().positive(),
    middle: z.number().positive(),
    lower: z.number().positive()
  }),
  movingAverages: z.object({
    sma20: z.number().positive(),
    sma50: z.number().positive(),
    sma200: z.number().positive(),
    ema12: z.number().positive(),
    ema26: z.number().positive()
  }),
  volume: z.object({
    current: z.number().min(0),
    average24h: z.number().min(0),
    ratio: z.number().min(0)
  }),
  timestamp: z.number()
});

/**
 * Trading Signal Schema
 */
export const TradingSignalSchema = z.object({
  tokenId: z.string(),
  tokenName: z.string(),
  action: z.enum(['buy', 'sell', 'hold']),
  confidence: z.number().min(0).max(100),
  reason: z.string(),
  currentPrice: z.number().positive(),
  suggestedPrice: z.number().positive().optional(),
  timeframe: z.string(),
  sources: z.object({
    technical: z.any().optional(),
    sentiment: AggregatedSentimentSchema.optional()
  }).optional(),
  timestamp: z.number()
});

/**
 * Backtest Historical Data Schema
 */
export const BacktestHistoricalDataSchema = z.object({
  symbol: z.string(),
  coinId: z.string(),
  prices: z.array(z.object({
    timestamp: z.number(),
    price: z.number().positive(),
    marketCap: z.number().positive().optional(),
    volume: z.number().min(0).optional()
  })),
  startDate: z.number(),
  endDate: z.number(),
  dataPoints: z.number().int().positive(),
  statistics: z.object({
    min: z.number().positive(),
    max: z.number().positive(),
    avg: z.number().positive(),
    median: z.number().positive(),
    volatility: z.number().min(0),
    volatilityPercent: z.number().min(0),
    totalReturn: z.number(),
    startPrice: z.number().positive(),
    endPrice: z.number().positive(),
    dataPoints: z.number().int().positive()
  })
});

/**
 * Backtest Trade Schema
 */
export const BacktestTradeSchema = z.object({
  timestamp: z.number(),
  action: z.enum(['buy', 'sell']),
  symbol: z.string(),
  price: z.number().positive(),
  quantity: z.number().positive(),
  positionValue: z.number().positive(),
  confidence: z.number().min(0).max(100),
  reason: z.string()
});

/**
 * Backtest Result Schema
 */
export const BacktestResultSchema = z.object({
  symbol: z.string(),
  startDate: z.number(),
  endDate: z.number(),
  initialCapital: z.number().positive(),
  finalCapital: z.number().positive(),
  totalReturn: z.number(),
  totalReturnPercent: z.number(),
  trades: z.array(BacktestTradeSchema),
  winningTrades: z.number().int().min(0),
  losingTrades: z.number().int().min(0),
  winRate: z.number().min(0).max(100),
  avgWinPercent: z.number(),
  avgLossPercent: z.number(),
  maxDrawdown: z.number(),
  maxDrawdownPercent: z.number().min(0).max(100),
  sharpeRatio: z.number(),
  profitFactor: z.number().min(0),
  benchmarkReturn: z.number(),
  excessReturn: z.number()
});

/**
 * Signal Explanation Schema
 */
export const SignalExplanationSchema = z.object({
  explanation: z.string(),
  confidence: z.number().min(0).max(100),
  // Optional fields for enhanced explanations
  signal: TradingSignalSchema.optional(),
  keyFactors: z.array(z.string()).optional(),
  risks: z.array(z.string()).optional(),
  educationalNote: z.string().optional(),
  timestamp: z.number().optional()
});

/**
 * Daily Sentiment Schema
 */
export const DailySentimentSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/), // YYYY-MM-DD format
  sentiment: z.number().min(-100).max(100),
  mentions: z.number().int().min(0),
  sources: z.object({
    twitter: z.number().min(-100).max(100),
    reddit: z.number().min(-100).max(100),
    news: z.number().min(-100).max(100)
  })
});

/**
 * Sentiment History Response Schema
 */
export const SentimentHistoryResponseSchema = z.object({
  symbol: z.string(),
  days: z.number().int().positive(),
  history: z.array(DailySentimentSchema),
  stats: z.object({
    bullishDays: z.number().int().min(0),
    bearishDays: z.number().int().min(0),
    neutralDays: z.number().int().min(0),
    avgSentiment: z.number().min(-100).max(100),
    maxSentiment: z.number().min(-100).max(100),
    minSentiment: z.number().min(-100).max(100),
    totalDays: z.number().int().positive()
  })
});

// ============================================
// COINGECKO API SCHEMAS
// ============================================

/**
 * Prices Schema (for CoinGecko)
 */
export const PricesSchema = z.record(
  z.string(),
  z.object({
    usd: z.number().positive(),
    usd_24h_change: z.number().optional(),
    eur: z.number().positive().optional(),
    eur_24h_change: z.number().optional()
  })
);

/**
 * Coin Search Result Schema
 */
export const CoinSchema = z.object({
  id: z.string(),
  symbol: z.string(),
  name: z.string(),
  market_cap_rank: z.number().int().nullable().optional(),
  thumb: z.string().nullable().optional(),
  large: z.string().nullable().optional()
});

export const CoinSearchResultSchema = z.object({
  coins: z.array(CoinSchema)
});

/**
 * Coin Details Schema
 */
export const CoinDetailsSchema = z.object({
  id: z.string(),
  symbol: z.string(),
  name: z.string(),
  description: z.object({
    en: z.string()
  }).optional(),
  image: z.object({
    thumb: z.string(),
    small: z.string(),
    large: z.string()
  }),
  market_data: z.object({
    current_price: z.object({
      usd: z.number().positive()
    }),
    market_cap: z.object({
      usd: z.number().positive()
    }).optional(),
    total_volume: z.object({
      usd: z.number().min(0)
    }).optional(),
    high_24h: z.object({
      usd: z.number().positive()
    }).optional(),
    low_24h: z.object({
      usd: z.number().positive()
    }).optional(),
    price_change_percentage_24h: z.number().optional(),
    circulating_supply: z.number().min(0).optional(),
    total_supply: z.number().min(0).optional(),
    max_supply: z.number().min(0).optional().nullable(),
    ath: z.object({
      usd: z.number().positive()
    }).optional(),
    atl: z.object({
      usd: z.number().positive()
    }).optional()
  }),
  links: z.object({
    homepage: z.array(z.string()).optional(),
    blockchain_site: z.array(z.string()).optional(),
    official_forum_url: z.array(z.string()).optional(),
    chat_url: z.array(z.string()).optional(),
    announcement_url: z.array(z.string()).optional(),
    twitter_screen_name: z.string().optional(),
    facebook_username: z.string().optional(),
    telegram_channel_identifier: z.string().optional(),
    subreddit_url: z.string().optional(),
    repos_url: z.object({
      github: z.array(z.string()).optional(),
      bitbucket: z.array(z.string()).optional()
    }).optional()
  }).optional()
});

/**
 * Chart Data Schema - more lenient to handle various CoinGecko responses
 */
export const ChartDataSchema = z.object({
  prices: z.array(z.tuple([z.number(), z.number()])).default([]),
  market_caps: z.array(z.tuple([z.number(), z.number()])).optional().default([]),
  total_volumes: z.array(z.tuple([z.number(), z.number()])).optional().default([])
});

// ============================================
// TRADING BOT API SCHEMAS (Autopilot)
// ============================================

/**
 * Portfolio Snapshot Schema
 */
export const PortfolioSnapshotSchema = z.object({
  totalValue: z.number().min(0),
  availableCash: z.number().min(0),
  positions: z.array(z.any()), // Will be refined when we have the Position schema
  dailyPnL: z.number(),
  dailyPnLPercent: z.number(),
  totalPnL: z.number(),
  totalPnLPercent: z.number()
});

/**
 * Strategy Schema
 */
export const StrategySchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.string(),
  status: z.enum(['active', 'paused', 'stopped', 'draft']),
  config: z.any(), // Strategy-specific configuration
  description: z.string().optional(),
  createdAt: z.string().or(z.date()),
  updatedAt: z.string().or(z.date())
});

/**
 * Trading Position Schema
 */
export const TradingPositionSchema = z.object({
  id: z.string(),
  strategyId: z.string(),
  symbol: z.string(),
  side: z.enum(['long', 'short']),
  status: z.enum(['open', 'closed', 'pending']),
  entryPrice: z.number().positive(),
  currentPrice: z.number().positive(),
  quantity: z.number().positive(),
  pnl: z.number(),
  pnlPercent: z.number(),
  openedAt: z.string().or(z.date()),
  closedAt: z.string().or(z.date()).optional()
});

/**
 * Autopilot API Response Schema
 */
export const AutopilotApiResponseSchema = <T extends z.ZodType>(dataSchema: T) =>
  z.object({
    success: z.boolean(),
    data: dataSchema.optional(),
    error: z.object({
      message: z.string(),
      code: z.string().optional(),
      details: z.any().optional()
    }).optional(),
    meta: z.object({
      page: z.number().int().optional(),
      limit: z.number().int().optional(),
      total: z.number().int().optional()
    }).optional()
  });

// ============================================
// VALIDATION HELPERS
// ============================================

/**
 * Validate data against a schema
 * @param schema - Zod schema to validate against
 * @param data - Data to validate
 * @param context - Context string for error logging
 * @returns Validated data or null if validation fails
 */
export function validateSchema<T>(
  schema: z.ZodType<T>,
  data: unknown,
  context: string = 'API Response'
): T | null {
  try {
    return schema.parse(data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      console.error(`${context} Validation Error:`, {
        issues: error.issues,
        received: data
      });
    } else {
      console.error(`${context} Unexpected Error:`, error);
    }
    return null;
  }
}

/**
 * Validate data against a schema (throws on error)
 * @param schema - Zod schema to validate against
 * @param data - Data to validate
 * @param context - Context string for error logging
 * @returns Validated data
 * @throws ZodError if validation fails
 */
export function validateSchemaStrict<T>(
  schema: z.ZodType<T>,
  data: unknown,
  context: string = 'API Response'
): T {
  try {
    return schema.parse(data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      console.error(`${context} Validation Error:`, {
        issues: error.issues,
        received: data
      });
    }
    throw error;
  }
}

// ============================================
// TYPE EXPORTS (for TypeScript)
// ============================================

export type TwitterSentiment = z.infer<typeof TwitterSentimentSchema>;
export type RedditSentiment = z.infer<typeof RedditSentimentSchema>;
export type NewsSentiment = z.infer<typeof NewsSentimentSchema>;
export type AggregatedSentiment = z.infer<typeof AggregatedSentimentSchema>;
export type TechnicalIndicators = z.infer<typeof TechnicalIndicatorsSchema>;
export type TradingSignal = z.infer<typeof TradingSignalSchema>;
export type BacktestHistoricalData = z.infer<typeof BacktestHistoricalDataSchema>;
export type BacktestResult = z.infer<typeof BacktestResultSchema>;
export type SignalExplanation = z.infer<typeof SignalExplanationSchema>;
export type SentimentHistoryResponse = z.infer<typeof SentimentHistoryResponseSchema>;
export type CoinDetails = z.infer<typeof CoinDetailsSchema>;
export type ChartData = z.infer<typeof ChartDataSchema>;
export type PortfolioSnapshot = z.infer<typeof PortfolioSnapshotSchema>;
export type Strategy = z.infer<typeof StrategySchema>;
export type TradingPosition = z.infer<typeof TradingPositionSchema>;
